import { formatFieldPath, isDescendantPath } from "@productlane/lib"

export type JsonKind =
	| "string"
	| "number"
	| "integer"
	| "boolean"
	| "null"
	| "array"
	| "object"

export interface SchemaField {
	/** Policy path, e.g. `metadata.note` or `items[].sku`. */
	path: string
	name: string
	depth: number
	type: string
	/** Every JSON type this accepts. Empty means the schema constrains nothing. */
	kinds: JsonKind[]
	nullable: boolean
	description?: string
	enumValues?: (string | number | boolean)[]
	required: boolean
	/** A container only groups its children; only a leaf takes a rule. */
	kind: "leaf" | "group"
	/** Element types when this is an array. */
	itemKinds?: JsonKind[]
}

const MAX_DEPTH = 4
const MAX_FIELDS = 200

type JsonObject = Record<string, unknown>

function isObject(value: unknown): value is JsonObject {
	return typeof value === "object" && value !== null && !Array.isArray(value)
}

function pointer(ref: string): string[] | null {
	if (!ref.startsWith("#/")) return null
	return ref
		.slice(2)
		.split("/")
		.map((part) => part.replace(/~1/g, "/").replace(/~0/g, "~"))
}

function followPointer(root: unknown, ref: string): unknown {
	const path = pointer(ref)
	if (!path) return null
	let cursor: unknown = root
	for (const part of path) {
		if (!isObject(cursor)) return null
		cursor = cursor[part]
	}
	return cursor
}

function resolve(
	root: unknown,
	schema: unknown,
	depth: number,
): JsonObject | null {
	if (depth > MAX_DEPTH + 2 || !isObject(schema)) return null

	if (typeof schema.$ref === "string") {
		return resolve(root, followPointer(root, schema.$ref), depth + 1)
	}

	if (Array.isArray(schema.allOf)) {
		const properties: JsonObject = {}
		const required: string[] = []
		for (const member of schema.allOf) {
			const merged = resolve(root, member, depth + 1)
			if (!merged) continue
			Object.assign(
				properties,
				isObject(merged.properties) ? merged.properties : {},
			)
			if (Array.isArray(merged.required))
				required.push(...(merged.required as string[]))
		}
		return { type: "object", ...schema, properties, required }
	}

	// A union is shown as its first branch: the form needs one shape to render, and
	// enforcement walks the real input anyway.
	for (const key of ["anyOf", "oneOf"] as const) {
		const branches = schema[key]
		if (Array.isArray(branches) && branches.length > 0) {
			const first = resolve(root, branches[0], depth + 1)
			if (first) return { ...schema, ...first }
		}
	}

	return schema
}

function describeType(schema: JsonObject): string {
	const type = schema.type
	if (typeof type === "string") return type
	if (Array.isArray(type)) {
		return type.filter((entry) => entry !== "null").join(" | ") || "any"
	}
	if (isObject(schema.properties)) return "object"
	if (schema.items !== undefined) return "array"
	return "any"
}

const JSON_KINDS: readonly string[] = [
	"string",
	"number",
	"integer",
	"boolean",
	"null",
	"array",
	"object",
]

export interface KindInfo {
	kinds: JsonKind[]
	nullable: boolean
}

const NO_KINDS: KindInfo = { kinds: [], nullable: false }

function pushKind(into: JsonKind[], kind: JsonKind): void {
	if (!into.includes(kind)) into.push(kind)
}

function kindOfLiteral(value: unknown): JsonKind | null {
	if (typeof value === "string") return "string"
	if (typeof value === "number") return "number"
	if (typeof value === "boolean") return "boolean"
	if (value === null) return "null"
	if (Array.isArray(value)) return "array"
	if (typeof value === "object") return "object"
	return null
}

function declaredKinds(type: unknown): KindInfo | null {
	if (typeof type === "string") {
		if (type === "null") return { kinds: [], nullable: true }
		return JSON_KINDS.includes(type)
			? { kinds: [type as JsonKind], nullable: false }
			: null
	}
	if (!Array.isArray(type)) return null

	const kinds: JsonKind[] = []
	let nullable = false
	for (const entry of type) {
		if (entry === "null") {
			nullable = true
			continue
		}
		if (typeof entry === "string" && JSON_KINDS.includes(entry)) {
			pushKind(kinds, entry as JsonKind)
		}
	}
	return { kinds, nullable }
}

function kindsFromEnum(values: unknown[], nullable: boolean): KindInfo {
	const kinds: JsonKind[] = []
	let sawNull = nullable
	for (const value of values) {
		const kind = kindOfLiteral(value)
		if (kind === "null") {
			sawNull = true
			continue
		}
		if (kind) pushKind(kinds, kind)
	}
	return { kinds, nullable: sawNull }
}

/**
 * Every JSON type an argument accepts, with null lifted into `nullable`. Unions are
 * read across all branches, unlike `resolve`, which keeps only the first.
 */
export function kindsOf(root: unknown, raw: unknown, depth: number): KindInfo {
	if (depth > MAX_DEPTH + 2 || !isObject(raw)) return NO_KINDS

	if (typeof raw.$ref === "string") {
		return kindsOf(root, followPointer(root, raw.$ref), depth + 1)
	}

	// OpenAPI 3.0 spells nullability as its own keyword rather than a type member.
	const flagged = raw.nullable === true
	const declared = declaredKinds(raw.type)
	if (declared && (declared.kinds.length > 0 || declared.nullable)) {
		return {
			kinds: declared.kinds,
			nullable: declared.nullable || flagged,
		}
	}

	for (const key of ["anyOf", "oneOf"] as const) {
		const branches = raw[key]
		if (!Array.isArray(branches) || branches.length === 0) continue
		const kinds: JsonKind[] = []
		let nullable = flagged
		for (const branch of branches) {
			const info = kindsOf(root, branch, depth + 1)
			for (const kind of info.kinds) pushKind(kinds, kind)
			if (info.nullable) nullable = true
		}
		return { kinds, nullable }
	}

	if (Array.isArray(raw.allOf) || isObject(raw.properties)) {
		return { kinds: ["object"], nullable: flagged }
	}
	if (raw.items !== undefined) return { kinds: ["array"], nullable: flagged }
	if (Array.isArray(raw.enum)) return kindsFromEnum(raw.enum, flagged)
	if ("const" in raw) return kindsFromEnum([raw.const], flagged)

	return { kinds: [], nullable: flagged }
}

function enumValuesOf(
	schema: JsonObject,
): (string | number | boolean)[] | undefined {
	if (!Array.isArray(schema.enum)) return undefined
	const values = schema.enum.filter(
		(value): value is string | number | boolean =>
			typeof value === "string" ||
			typeof value === "number" ||
			typeof value === "boolean",
	)
	return values.length > 0 ? values : undefined
}

function join(prefix: string, name: string): string {
	const segment = formatFieldPath([{ kind: "key", key: name }])
	if (!prefix) return segment
	return segment.startsWith("[")
		? `${prefix}${segment}`
		: `${prefix}.${segment}`
}

/**
 * Projects a tool's advertised JSON Schema into the rows the policy form renders.
 * Returns an empty list when the schema declares no properties, which is the cue
 * to fall back to hand-typed paths.
 */
export function flattenToolSchema(rawSchema: unknown): SchemaField[] {
	const root = rawSchema
	const fields: SchemaField[] = []

	const walk = (schema: unknown, prefix: string, depth: number): void => {
		if (depth > MAX_DEPTH || fields.length >= MAX_FIELDS) return
		const resolved = resolve(root, schema, depth)
		if (!resolved || !isObject(resolved.properties)) return

		const required = new Set(
			Array.isArray(resolved.required) ? (resolved.required as string[]) : [],
		)

		for (const [name, rawChild] of Object.entries(resolved.properties)) {
			if (fields.length >= MAX_FIELDS) return
			const child = resolve(root, rawChild, depth + 1)
			if (!child) continue

			const path = join(prefix, name)
			const type = describeType(child)
			const description =
				typeof child.description === "string" ? child.description : undefined

			const items = child.items
			const itemSchema: unknown = Array.isArray(items) ? items[0] : items
			const resolvedItems = itemSchema
				? resolve(root, itemSchema, depth + 1)
				: null
			const arrayOfObjects =
				!!resolvedItems && isObject(resolvedItems.properties)
			const isGroup = isObject(child.properties) || arrayOfObjects

			// Read off the raw child so a union contributes every branch, not just the
			// first one `resolve` keeps.
			const { kinds, nullable } = kindsOf(root, rawChild, depth + 1)
			const itemKinds = itemSchema
				? kindsOf(root, itemSchema, depth + 1).kinds
				: undefined

			fields.push({
				path,
				name,
				depth,
				type,
				kinds,
				nullable,
				itemKinds,
				description,
				enumValues: enumValuesOf(child),
				required: required.has(name),
				kind: isGroup ? "group" : "leaf",
			})

			if (arrayOfObjects) {
				walk(resolvedItems, `${path}[]`, depth + 1)
			} else if (isObject(child.properties)) {
				walk(child, path, depth + 1)
			}
		}
	}

	walk(root, "", 0)
	return fields
}

/** Immediate children of a container row, for the object editor's key hints. */
export function childFieldsOf(
	fields: readonly SchemaField[],
	group: SchemaField,
): SchemaField[] {
	return fields.filter(
		(field) =>
			field.depth === group.depth + 1 &&
			isDescendantPath(field.path, group.path),
	)
}
